import { axiosRequest } from '@/plugins/axios-common'
import { handleError } from '@/store/handle-error'

export const ENGAGEMENT_LETTER_TEMPLATE_NAMESPACE = 'engagementLetterTemplate'

const state = _ => ({
  engagementLetterTemplates: [],
  languages: [],
  isLoading: false,
})

const mutations = {
  setEngagementLetterTemplates: (state, templates) => {
    state.engagementLetterTemplates = templates
  },

  addEngagementLetterTemplate: (state, template) => {
    state.engagementLetterTemplates.push(template)
  },

  updateEngagementLetterTemplate: (state, updatedTemplate) => {
    const index = state.engagementLetterTemplates.findIndex(t => t.id === updatedTemplate.id)
    if (index !== -1) {
      state.engagementLetterTemplates.splice(index, 1, updatedTemplate)
    }
  },

  removeEngagementLetterTemplate: (state, templateId) => {
    state.engagementLetterTemplates = state.engagementLetterTemplates.filter(t => t.id !== templateId)
  },

  setLoading: (state, loading) => {
    state.isLoading = loading
  },

  setLanguages: (state, languages) => {
    state.languages = languages
  },

  // Translation management mutations
  setEngagementLetterTemplateTranslation: (state, payload) => {
    const template = state.engagementLetterTemplates.find(t => t.id === payload.engagement_letter_template_id)
    if (!template) return

    const idx = template.engagement_letter_template_translations.findIndex(t => t.id === payload.id)
    if (idx < 0) {
      template.engagement_letter_template_translations.push(payload)
    } else {
      template.engagement_letter_template_translations.splice(idx, 1, payload)
    }
  },

  removeEngagementLetterTemplateTranslation: (state, { templateId, translationId }) => {
    const template = state.engagementLetterTemplates.find(t => t.id === templateId)
    if (!template) return

    const idx = template.engagement_letter_template_translations.findIndex(t => t.id === translationId)
    if (idx >= 0) {
      template.engagement_letter_template_translations.splice(idx, 1)
    }
  },
}

const actions = {
  getEngagementLetterTemplates ({ commit }, params = {}) {
    commit('setLoading', true)

    return axiosRequest.get('/engagement_letter_templates', { params }).
      then(({ data }) => {
        commit('setEngagementLetterTemplates', data)
        return data
      }).
      catch(error => handleError(error)).
      finally(_ => commit('setLoading', false))
  },

  async getEngagementLetterTemplate (_, templateId) {
    try {
      const { data } = await axiosRequest.get(`/engagement_letter_templates/${templateId}`)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  },

  async createEngagementLetterTemplate ({ commit }, templateData) {
    try {
      const { data } = await axiosRequest.post('/engagement_letter_templates', {
        engagement_letter_template: templateData,
      })
      commit('addEngagementLetterTemplate', data)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  },

  async updateEngagementLetterTemplate ({ commit }, { id, templateData }) {
    try {
      const { data } = await axiosRequest.patch(`/engagement_letter_templates/${id}`, {
        engagement_letter_template: templateData,
      })
      commit('updateEngagementLetterTemplate', data)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  },

  async deleteEngagementLetterTemplate ({ commit }, templateId) {
    try {
      await axiosRequest.delete(`/engagement_letter_templates/${templateId}`)
      commit('removeEngagementLetterTemplate', templateId)
      return true
    } catch (error) {
      handleError(error)
      throw error
    }
  },

  async getEngagementLetterTemplateLanguages ({ commit }) {
    try {
      const { data } = await axiosRequest.get('/engagement_letter_templates/languages')
      commit('setLanguages', data)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  },

  // Translation management actions
  async createEngagementLetterTemplateTranslation ({ commit }, { templateId, translation }) {
    try {
      const { data } = await axiosRequest.post(
        `/engagement_letter_templates/${templateId}/translations`,
        { engagement_letter_template_translation: translation }
      )
      commit('setEngagementLetterTemplateTranslation', data)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  },

  async updateEngagementLetterTemplateTranslation ({ commit }, { templateId, translation }) {
    try {
      const { data } = await axiosRequest.patch(
        `/engagement_letter_templates/${templateId}/translations/${translation.id}`,
        { engagement_letter_template_translation: translation }
      )
      commit('setEngagementLetterTemplateTranslation', data)
      return data
    } catch (error) {
      handleError(error)
      throw error
    }
  },

  async deleteEngagementLetterTemplateTranslation ({ commit }, { templateId, translationId }) {
    try {
      await axiosRequest.delete(`/engagement_letter_templates/${templateId}/translations/${translationId}`)
      commit('removeEngagementLetterTemplateTranslation', { templateId, translationId })
      return true
    } catch (error) {
      handleError(error)
      throw error
    }
  },
}

const getters = {
  engagementLetterTemplates: state => state.engagementLetterTemplates,
  engagementLetterLanguages: state => state.languages,
  isLoading: state => state.isLoading,

  templateById: state => templateId => {
    return state.engagementLetterTemplates.find(template => template.id === templateId)
  },
}

export const EngagementLetterTemplate = {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
